import React, {
    CSSProperties,
    ReactElement,
    MutableRefObject,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    PropsWithChildren,
    ReactNode
} from 'react';
import { ArrowLeft24 } from '@honeybook/icons';
import { createPortal } from 'react-dom';
import { AnimatePresence, HTMLMotionProps, motion } from 'framer-motion';
import HBModalCloseButton from './HBModalCloseButton';
import { useIPhoneSafariStatusBarColor } from '../../hooks/useIPhoneSafariStatusBarColor';
import { vars } from '../../theme';
import HBButton, { IButtonProps } from '../HBButton/HBButton';
import { HBButtonTheme } from '../HBButton/HBButton.types';
import {
    Actions,
    ActionSpacer,
    BackIcon,
    Body,
    CheckboxAndActions,
    ChildModalOverlay,
    Container,
    FooterExtraContent,
    Header,
    HeaderButton,
    HeaderActionsContainer,
    HeaderLeftSection,
    HeaderRightSection,
    HeaderTitle,
    Modal,
    PaddingDiv,
    StyledConfetti,
    StyledHBField,
    StyledHeaderButtonText
} from './HBModal.styles';
import HBCheckbox from '../HBCheckbox/HBCheckbox';
import { TTextAlignment } from '../HBText/HBText.types';
import { guid as uuidv4, isFunction } from '../../utils/shared.utils';
import { useWindowSize } from '../../hooks/useWindowSize';
import { TourPixel } from '../HBTour/HBTourTooltip.styles';
import { useScrollToElementEffect } from '../../hooks/useScrollToElementEffect';
import { IActionTestIdProps } from '../common/internal/test-id.types';
import HBTooltip, { ITooltipProps } from '../HBTooltip/HBTooltip';
import { MODAL_ANIMATION } from './HBModal.styles';

export interface IAction
    extends Omit<IButtonProps, 'children' | 'testId' | 'testIdType'>,
        IActionTestIdProps {
    theme?: HBButtonTheme;
    text: string;
    /** Replaces the default HBButton with a custom button component for this action. */
    customButton?: React.ComponentType<
        Omit<IButtonProps, 'testId' | 'testIdName'>
    >;
    /** Props spread onto the HBTooltip wrapping this action button. */
    tooltipProps?: Partial<ITooltipProps>;
    /** Content rendered inside the tooltip; tooltip only appears when this is provided. */
    tooltipContent?: ReactNode;
}

export interface IHeaderAction {
    onClick: () => void;
    icon?: ReactElement;
    text?: string;
}

export interface ICheckbox {
    id: string;
    label: string | ReactElement;
    checked: boolean;
    onClick: () => void;
}

export type TModalOnCloseSource = 'backdropClick' | 'closeButton' | 'escapeKey';

type TAlignmentSides = Exclude<TTextAlignment, 'center'>;
export interface IModal {
    /** Footer action buttons rendered below the body. */
    actions?: IAction[];
    /** Horizontal alignment of the footer action buttons. Defaults to `'right'`. */
    actionsAlignment?: TTextAlignment;
    /** Inline styles applied to the footer container wrapping all action buttons. */
    actionsContainerStyles?: CSSProperties;
    /** Inline styles applied to each action button wrapper element. */
    actionsStyles?: CSSProperties;
    /** Inline styles applied to the scrollable modal body container. */
    bodyStyles?: CSSProperties;
    /** Renders a checkbox in the footer alongside the action buttons. */
    checkbox?: ICheckbox;
    /** CSS class applied to the root element. */
    className?: string;
    /** Text label displayed next to the close icon, replacing the icon-only close button. */
    closeButtonText?: string;
    /** Tooltip text shown on hover over the close button. */
    closeButtonTooltipText?: string;
    /** Color override for the close button icon. */
    closeIconColor?: string;
    /** Inline styles applied to the modal container element. */
    containerStyles?: CSSProperties;
    /** Forces mobile layout regardless of viewport size. */
    forceMobile?: boolean;
    /** Renders a semi-transparent overlay inside the modal, used when a child modal is open on top. */
    hasChildModalOverlay?: boolean;
    /** Custom actions rendered in the header bar, either as an array of icon+text buttons or arbitrary ReactNode. */
    headerActions?: IHeaderAction[] | ReactNode;
    /** Which side of the header to place headerActions; only 'left' or 'right'. */
    headerActionsAlignment?: TAlignmentSides;
    /** Removes the backdrop overlay behind the modal. */
    hideBackdrop?: boolean;
    /** Hides the close button. Use only when an action button or `isRequiredPrompt` provides a clear dismissal path. */
    hideCloseButton?: boolean;
    /** Expands the modal to nearly fill the viewport while retaining some margin. */
    isAlmostFullScreen?: boolean;
    /** Marks this as a nested modal, applying elevated box-shadow and transparent backdrop. */
    isChildModal?: boolean;
    /** Reserves header space without displaying a title, useful when only a close button or back arrow is needed. */
    isEmptyTitle?: boolean;
    /** Expands the modal to fill the entire viewport on mobile breakpoints. */
    isFullScreenOnMobile?: boolean;
    /** Controls whether the modal is visible. Toggle this to open and close the modal. */
    isOpen: boolean;
    /** Prevents closing via backdrop click or Escape key, forcing the user to interact with modal actions. */
    isRequiredPrompt?: boolean;
    /** Renders a confetti animation overlay inside the modal. */
    isWithConfetti?: boolean;
    /** Maximum height of the modal body on desktop. Defaults to `'90%'`; ignored on mobile when `isFullScreenOnMobile` is true. */
    maxHeight?: string;
    /** Maximum width of the modal. Defaults to `'1075px'`; use a narrower value for confirmation dialogs with short copy. */
    maxWidth?: string;
    /** External ref for the scrollable body container, useful for programmatic scroll control. */
    modalBodyRef?: MutableRefObject<HTMLDivElement>;
    /** Framer Motion animation props that override the default modal enter/exit animation. */
    modalCustomAnimation?: HTMLMotionProps<'div'>;
    /** Skips the backdrop fade-in animation on open while preserving the exit animation. */
    noBackdropAnimationOnOpen?: boolean;
    /** Disables the scrollable body container, allowing content to determine the modal's height. */
    noInnerScroll?: boolean;
    /** Renders a back arrow in the header that triggers this callback. */
    onBack?(): void;
    /** Called when the backdrop is clicked; if not provided, onClose is called instead with source 'backdropClick'. */
    onBackdropClick?(): void;
    /** Called when the modal requests to close. Receives `{ source }` indicating whether the trigger was `'backdropClick'`, `'closeButton'`, or `'escapeKey'`. */
    onClose?: (props?: { source?: TModalOnCloseSource }) => void;
    /** Fires after the modal's exit animation completes, before resetForm is called. */
    onModalFadeOutComplete?: () => void;
    /** Called as the modal body scrolls. Use to track scroll position or implement scroll-based effects. */
    onScroll?(event): void;
    /** Wraps the modal content in a form element and calls this on form submission. */
    submitForm?: () => void;
    /** Called after the modal exit animation completes, intended for clearing form state. */
    resetForm?: () => void;
    /** Disables modal interactions and blocks backdrop close; also shows loading state on submit-type action buttons. */
    isSubmitting?: boolean;
    /** DOM element to portal the modal into; defaults to document.body. */
    parent?: HTMLElement;
    /** Element or selector to scroll into view when the modal opens. */
    scrollToRef?: string | HTMLElement | MutableRefObject<HTMLElement>;
    /** Explicit test ID string; sets `data-testid` directly. */
    testId?: string;
    /** Modal header title. Accepts a string or ReactElement for custom header content. */
    title?: string | ReactElement;
    /** Horizontal alignment of the title in the modal header. Defaults to `'center'`. */
    titleAlignment?: TTextAlignment;
    /** Additional content rendered in the footer area, positioned before the action buttons. */
    footerExtraContent?: ReactNode;
    /** HTML id applied to the backdrop container element. */
    backdropId?: string;
    /** Makes the modal container inert (non-interactive and hidden from assistive technology) without unmounting it. */
    inert?: boolean;
    /** Applies a blur filter to the backdrop overlay. */
    blurBackdrop?: boolean;
    /** Applies AI-themed gradient background and styling to the modal. */
    theme?: 'ai';
    /** Duration in ms before the confetti animation stops recycling; only applies when isWithConfetti is true. */
    confettiDurationInMs?: number;
}

/**
 * Context that provides a default `parent` portal target for all HBModal instances
 * in the subtree. When an explicit `parent` prop is passed to HBModal it always wins;
 * the context is only used as a fallback.
 *
 * Use case: scope modal backdrops to a specific DOM element (e.g. an app-area card)
 * so that a side panel or overlay remains visible and interactive behind the backdrop.
 *
 * @example
 * <HBModalParentContext.Provider value={appAreaElement}>
 *   {children}
 * </HBModalParentContext.Provider>
 */
export const HBModalParentContext = createContext<HTMLElement | null>(null);

const CHILD_MODAL_STYLES = {
    modal: {
        boxShadow: vars.shadow['50']
    },
    container: {
        backgroundColor: 'unset'
    }
};

function HeaderActionButton({ headerAction }: { headerAction: IHeaderAction }) {
    const { onClick, icon, text } = headerAction;

    return (
        <HeaderButton onClick={onClick}>
            {icon}
            <StyledHeaderButtonText
                type="emphasis14"
                color="text.tertiary"
                data-testidtype="hb-modal-header-action--button"
            >
                {text}
            </StyledHeaderButtonText>
        </HeaderButton>
    );
}

function ActionButton({
    action,
    isMediumUp,
    isSubmitting
}: {
    action: IAction;
    isMediumUp: boolean;
    isSubmitting: boolean;
}) {
    const {
        customButton,
        scope = 'system',
        size = 'sm',
        testIdName,
        tooltipContent,
        tooltipProps,
        type,
        loading,
        ...actionProps
    } = action;

    const ButtonComponent = (customButton ||
        HBButton) as React.ComponentType<IButtonProps>;
    const buttonTheme = customButton ? 'custom' : action.theme;

    return (
        <HBTooltip
            disabled={!tooltipContent}
            target={
                <ActionSpacer $isMediumUp={isMediumUp}>
                    <ButtonComponent
                        size={size}
                        scope={scope}
                        fullWidth
                        buttonTheme={buttonTheme}
                        testId={
                            testIdName
                                ? `hb-modal-${testIdName}--button`
                                : undefined
                        }
                        testIdType="hb-modal-action--button"
                        type={type}
                        loading={loading || (type === 'submit' && isSubmitting)}
                        {...actionProps}
                    >
                        {action.text}
                    </ButtonComponent>
                </ActionSpacer>
            }
            {...tooltipProps}
        >
            {tooltipContent}
        </HBTooltip>
    );
}

const HBModal = ({
    actions,
    actionsAlignment = 'right',
    actionsContainerStyles,
    actionsStyles,
    bodyStyles,
    checkbox,
    children,
    className,
    closeButtonText,
    closeButtonTooltipText,
    closeIconColor,
    containerStyles,
    forceMobile,
    hasChildModalOverlay,
    headerActions,
    headerActionsAlignment = 'left',
    hideBackdrop,
    hideCloseButton,
    isAlmostFullScreen = false,
    isChildModal,
    isEmptyTitle = false,
    isFullScreenOnMobile = false,
    isOpen,
    isRequiredPrompt = false,
    isWithConfetti = false,
    maxHeight = '90%',
    maxWidth = '1075px',
    modalBodyRef,
    modalCustomAnimation,
    noBackdropAnimationOnOpen,
    noInnerScroll = false,
    onBack,
    onBackdropClick,
    onClose,
    onModalFadeOutComplete: _onModalFadeOutComplete,
    submitForm,
    resetForm,
    isSubmitting,
    onScroll,
    parent,
    scrollToRef,
    testId = 'hb-modal',
    title = null,
    titleAlignment = 'center',
    footerExtraContent,
    backdropId,
    inert,
    blurBackdrop,
    theme,
    confettiDurationInMs = 10000
}: PropsWithChildren<IModal>) => {
    const _modalBodyRef = useRef(null);
    const __modalBodyRef = modalBodyRef || _modalBodyRef;
    const isModalFocused = useRef(false);
    // Explicit prop wins; context is the fallback; document.body is the default.
    const contextParent = useContext(HBModalParentContext);
    const resolvedParent = parent ?? contextParent ?? undefined;
    const hasParent = resolvedParent != null;

    const shouldAddTitleSection = !!title || isEmptyTitle;

    const hasActions = !!actions || !!checkbox;

    const isAiModal = theme === 'ai';

    const handleBackdropClick = e => {
        e.stopPropagation(); // Handling modal over modal situation
        if (
            !isSubmitting &&
            e.target === e.currentTarget && // if the click is on the backdrop itself
            !isRequiredPrompt &&
            !isModalFocused.current // handling situation where the cursor leaves the modal while marking text
        ) {
            if (isFunction(onBackdropClick)) {
                onBackdropClick();
            } else if (isFunction(onClose)) {
                onClose({ source: 'backdropClick' });
            }
        }
        isModalFocused.current = false;
    };

    const handleKey = useCallback(
        e => {
            if (
                !hasChildModalOverlay &&
                isOpen &&
                e.key === 'Escape' &&
                isFunction(onClose)
            ) {
                onClose({ source: 'escapeKey' });
            }
        },
        [hasChildModalOverlay, isOpen, onClose]
    );

    const { isMediumUp } = useWindowSize();

    // This is a temp variable in order to use isFullScreenOnMobile as a feature toggle.
    // This should be converted to isMediumUp only
    const isMediumUpOrForceDesktop = isMediumUp || !isFullScreenOnMobile;

    useEffect(() => {
        if (!isRequiredPrompt && isFunction(onClose)) {
            document.addEventListener('keyup', handleKey);
            return () => {
                document.removeEventListener('keyup', handleKey);
            };
        }
    }, [isRequiredPrompt, onClose, handleKey]);

    const [isConfettiRecycle, setIsConfettiRecycle] = useState(true);

    // eslint-disable-next-line consistent-return
    useEffect(() => {
        if (isOpen && isWithConfetti) {
            const timeout = setTimeout(() => {
                setIsConfettiRecycle(false);
            }, confettiDurationInMs);
            return () => {
                clearTimeout(timeout);
                setIsConfettiRecycle(true);
            };
        }
    }, [isOpen, isWithConfetti, confettiDurationInMs]);

    useIPhoneSafariStatusBarColor({
        isActive: isOpen && !isFullScreenOnMobile && !isChildModal,
        // Unsupported: platform meta color — no semantic equivalent (see .claude/rules/styling.md)
        color: '#4C4C4C' // this is the resulting color when the modal background is on top of a white background
    });

    useScrollToElementEffect(scrollToRef, isOpen);

    const backdropAnimation = {
        ...MODAL_ANIMATION,
        initial: !noBackdropAnimationOnOpen && MODAL_ANIMATION.initial
    };

    const mergedContainerStyles = {
        ...(containerStyles && containerStyles),
        ...(isChildModal && CHILD_MODAL_STYLES.container)
    };

    const modalStyles = {
        ...(isChildModal && CHILD_MODAL_STYLES.modal)
    };

    const showCloseButton =
        !isRequiredPrompt && isFunction(onClose) && !hideCloseButton;

    const headerActionsElements = Array.isArray(headerActions)
        ? (headerActions as IHeaderAction[]).map(headerAction => (
              <HeaderActionButton
                  headerAction={headerAction}
                  key={headerAction.text || uuidv4()}
              />
          ))
        : headerActions;

    const header = (
        <AnimatePresence>
            {shouldAddTitleSection && (
                <Header
                    key="header"
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    $isEmptyTitle={isEmptyTitle}
                >
                    <TourPixel data-tour-id="modal_title" />
                    <HeaderLeftSection>
                        {isFunction(onBack) && (
                            <BackIcon
                                onClick={onBack}
                                data-testid="hb-modal-back--button"
                            >
                                <ArrowLeft24 color={vars.colors.text.primary} />
                            </BackIcon>
                        )}
                        {headerActions && headerActionsAlignment === 'left' && (
                            <HeaderActionsContainer>
                                {headerActionsElements}
                            </HeaderActionsContainer>
                        )}
                    </HeaderLeftSection>
                    <HeaderTitle
                        $alignment={titleAlignment}
                        {...(title ? { 'data-testid': 'hb-modal-title' } : {})}
                    >
                        {title || ''}
                    </HeaderTitle>
                    <HeaderRightSection>
                        {headerActions &&
                            headerActionsAlignment === 'right' && (
                                <HeaderActionsContainer>
                                    {headerActionsElements}
                                </HeaderActionsContainer>
                            )}
                        {showCloseButton && (
                            <HBModalCloseButton
                                onClose={() =>
                                    onClose({ source: 'closeButton' })
                                }
                                data-testid="hb-modal-close--button"
                                closeButtonText={closeButtonText}
                                closeButtonTooltipText={closeButtonTooltipText}
                                closeIconColor={closeIconColor}
                            />
                        )}
                    </HeaderRightSection>
                </Header>
            )}
            {!shouldAddTitleSection && showCloseButton && (
                <HBModalCloseButton
                    onClose={() => onClose({ source: 'closeButton' })}
                    data-testid="hb-modal-close--button"
                    closeButtonText={closeButtonText}
                    closeButtonTooltipText={closeButtonTooltipText}
                    closeIconColor={closeIconColor}
                />
            )}
            {!shouldAddTitleSection && isFunction(onBack) && (
                <BackIcon onClick={onBack} data-testid="hb-modal-back--button">
                    <ArrowLeft24 color={vars.colors.text.primary} />
                </BackIcon>
            )}
        </AnimatePresence>
    );

    const confetti = isWithConfetti && (
        <StyledConfetti recycle={isConfettiRecycle} />
    );

    const body = (
        <Body
            ref={__modalBodyRef}
            $hasActions={hasActions}
            $noInnerScroll={noInnerScroll}
            $isAlmostFullScreen={isAlmostFullScreen}
            style={bodyStyles}
            $isEmptyTitle={isEmptyTitle}
            {...(!noInnerScroll && !!onScroll && { onScroll })}
        >
            {children}
        </Body>
    );

    const checkboxAndActions = hasActions && (
        <CheckboxAndActions
            $hasCheckbox={!!checkbox || !!footerExtraContent}
            $actionsAlignment={actionsAlignment}
            style={actionsContainerStyles}
            $forceMobile={forceMobile}
        >
            {checkbox && (
                <StyledHBField
                    id={checkbox.id}
                    inline
                    width={isMediumUpOrForceDesktop ? '278px' : 'initial'}
                    marginLeft="10px"
                    marginTop="12px"
                    marginBottom="0px"
                    label={checkbox.label}
                    size="xs"
                    placement="left"
                >
                    <HBCheckbox
                        key={checkbox.id}
                        id={checkbox.id}
                        testId={checkbox.id}
                        name={checkbox.id}
                        checked={checkbox.checked}
                        size="sm"
                        onChange={checkbox.onClick}
                    />
                </StyledHBField>
            )}
            {footerExtraContent && (
                <FooterExtraContent>{footerExtraContent}</FooterExtraContent>
            )}
            <Actions
                style={actionsStyles}
                $isMediumUp={isMediumUpOrForceDesktop}
            >
                {actions.map((action, index) => (
                    <ActionButton
                        key={index}
                        action={action}
                        isMediumUp={isMediumUpOrForceDesktop}
                        isSubmitting={isSubmitting}
                    />
                ))}
            </Actions>
        </CheckboxAndActions>
    );

    const childModalOverlay = (
        <AnimatePresence>
            {hasChildModalOverlay && <ChildModalOverlay {...MODAL_ANIMATION} />}
        </AnimatePresence>
    );

    const modal = (
        <AnimatePresence>
            <Modal
                role="dialog"
                aria-modal
                data-testid={testId}
                $maxWidth={isMediumUpOrForceDesktop ? maxWidth : 'none'}
                $maxHeight={isMediumUpOrForceDesktop ? maxHeight : 'none'}
                className={className}
                $noInnerScroll={noInnerScroll}
                $isAlmostFullScreen={isAlmostFullScreen}
                $isMediumUp={!forceMobile && isMediumUpOrForceDesktop}
                $isFullViewport={
                    isFullScreenOnMobile && !forceMobile && !isMediumUp
                }
                $isAiModal={isAiModal}
                style={modalStyles}
                onMouseDown={() => (isModalFocused.current = true)}
                inert={inert || isSubmitting}
                {...modalCustomAnimation}
                {...(submitForm && {
                    as: motion.form as any,
                    noValidate: true,
                    onSubmit: e => {
                        e.preventDefault();
                        submitForm();
                    }
                })}
            >
                {childModalOverlay}
                {header}
                {body}
                {checkboxAndActions}
            </Modal>
        </AnimatePresence>
    );

    const content = (
        <>
            {confetti}
            {modal}
            {noInnerScroll && <PaddingDiv />}
        </>
    );

    const onModalFadeOutComplete = () => {
        _onModalFadeOutComplete?.();
        resetForm?.();
    };

    const wrapper = (
        <AnimatePresence onExitComplete={onModalFadeOutComplete}>
            {isOpen && (
                <>
                    {hideBackdrop ? (
                        <motion.div {...MODAL_ANIMATION}>{content}</motion.div>
                    ) : (
                        <Container
                            id={backdropId}
                            onClick={handleBackdropClick}
                            {...backdropAnimation}
                            $hasParent={hasParent}
                            $forceMobile={forceMobile}
                            $noInnerScroll={noInnerScroll}
                            style={mergedContainerStyles}
                            $blurBackdrop={blurBackdrop}
                            $isAiModal={isAiModal}
                        >
                            {content}
                        </Container>
                    )}
                </>
            )}
        </AnimatePresence>
    );

    const modalContainer = resolvedParent || document.body;

    return createPortal(wrapper, modalContainer);
};

export default HBModal;
